// ── 결과 입력 모달 컴포넌트 ──────────────────────────────────────────────────
// - 피드백 옵션 그리드 (API 반환값 기반)
// - 고정 액션: 미뤄짐 / 미팅인계
// - 미뤄짐: 날짜 지정 / 날짜 미정(킵) 탭
// - 미팅인계: 담당자 선택
// - 메모 textarea
// - 계약완료 토글 → ContractSection 렌더
// - 저장 버튼: 계약완료 시 확인 모달 경유

import React from 'react';
import Modal from '../../../components/Modal';
import { ContractSection } from './ContractSection';
import { TXT, getKstTodayYmd, addDaysYmd } from '../../../components/meetingmap/utils';
import {
  ghostActionStyle, primaryActionStyle,
  modalFieldWrapStyle, modalSectionStyle, modalSectionLabelStyle,
  feedbackGridStyle, feedbackOptionBtnStyle,
  fixedActionRowStyle, fixedActionBtnStyle,
  feedbackMemoStyle, modalDividerStyle,
  contractToggleBtnStyle,
  subOptionWrapStyle, postponeTabBtnStyle, quickDateBtnStyle,
  subOptionSelectedStyle, salesPersonGridStyle, salesPersonBtnStyle,
  modalInputStyle, emptyHintStyle, warnHintStyle,
} from '../../../components/meetingmap/styles';

export function ResultModal({
  open,
  onClose,
  onSave,
  resultForm,
  setResultForm,
  sortedFeedbackOptions,  // 고정 키 제외 피드백 옵션 배열
  salesPersons,           // 미팅인계 대상자 목록
  contractTicketTypes,    // 계약완료 상품 목록
  savingResult,
  originalIsKeep,         // 마케터 킵 해제 불가 판단용
  adminMode,
  fetchContractTicketTypes, // 계약완료 토글 시 lazy fetch
  hasValiditySetting,       // (name) => bool
  onConfirmContract,        // 계약완료 저장 전 확인 모달 열기 콜백
}) {
  return (
    <Modal
      open={open}
      title={TXT.resultTitle}
      icon="📝"
      onClose={onClose}
      width="min(480px, 96vw)"
      mobileSheet
      footer={(
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" style={ghostActionStyle} onClick={onClose}>
            {TXT.close}
          </button>
          <button
            type="button"
            style={primaryActionStyle}
            // 미팅인계인데 담당자를 안 골랐으면 저장을 막는다(서버도 400 으로 거부한다).
            disabled={savingResult || (resultForm.feedback === '미팅인계' && !resultForm.new_sales_person_name)}
            onClick={() => resultForm.is_contracted ? onConfirmContract() : onSave()}
          >
            {savingResult ? TXT.saving : TXT.save}
          </button>
        </div>
      )}
    >
      <div style={modalFieldWrapStyle}>

        {/* ── 피드백 섹션 ────────────────────────────────────── */}
        <div style={modalSectionStyle}>
          <div style={modalSectionLabelStyle}>피드백</div>
          {/* 항목이 0개면 빈 칸만 남아 "왜 아무것도 없지?"가 된다 */}
          {sortedFeedbackOptions.length === 0 && (
            <div style={emptyHintStyle}>
              피드백 항목이 아직 설정되지 않았습니다. 미팅지도 관리 ⚙️ 설정에서 추가할 수 있습니다.
            </div>
          )}
          <div style={feedbackGridStyle}>
            {sortedFeedbackOptions.map((option) => {
              const isSelected = resultForm.feedback === option.name;
              return (
                <button
                  key={option.name}
                  type="button"
                  style={feedbackOptionBtnStyle(isSelected, false, option.color_key)}
                  onClick={() =>
                    setResultForm((prev) => {
                      const next = prev.feedback === option.name ? '' : option.name;
                      return {
                        ...prev,
                        feedback: next,
                        // 피드백 선택 시 계약완료와 상호 배타적
                        ...(next ? { is_contracted: false } : {}),
                        // auto_keep 옵션 선택 시 자동으로 킵 등록
                        ...(next && option.auto_keep ? { is_keep: true } : {}),
                      };
                    })
                  }
                >
                  {option.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── 고정 액션: 미뤄짐 / 미팅인계 ─────────────────────── */}
        <div style={fixedActionRowStyle}>
          <button
            type="button"
            style={fixedActionBtnStyle(resultForm.feedback === '미뤄짐')}
            onClick={() =>
              setResultForm((prev) => {
                const on = prev.feedback !== '미뤄짐';
                return { ...prev, feedback: on ? '미뤄짐' : '', new_visit_date: '', ...(on ? { is_contracted: false } : {}) };
              })
            }
          >미뤄짐</button>
          <button
            type="button"
            style={fixedActionBtnStyle(resultForm.feedback === '미팅인계')}
            onClick={() =>
              setResultForm((prev) => {
                const on = prev.feedback !== '미팅인계';
                return { ...prev, feedback: on ? '미팅인계' : '', new_sales_person_name: '', ...(on ? { is_contracted: false } : {}) };
              })
            }
          >미팅인계</button>
        </div>

        {/* ── 미뤄짐 서브옵션 ─────────────────────────────────── */}
        {resultForm.feedback === '미뤄짐' && (
          <div style={subOptionWrapStyle}>
            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
              <button
                type="button"
                style={postponeTabBtnStyle(!resultForm.is_keep)}
                // 마케터가 이미 킵된 것은 날짜 지정으로 변경 불가 (해제 = TM직원만)
                disabled={originalIsKeep && !adminMode}
                onClick={() => setResultForm((prev) => ({ ...prev, is_keep: false }))}
              >날짜 지정</button>
              <button
                type="button"
                style={postponeTabBtnStyle(resultForm.is_keep)}
                onClick={() => setResultForm((prev) => ({ ...prev, is_keep: true, new_visit_date: '' }))}
              >날짜 미정</button>
            </div>
            {/* 날짜 지정 */}
            {!resultForm.is_keep && (
              <>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    type="date"
                    style={{ ...modalInputStyle, width: 136, padding: '5px 8px', fontSize: 12, flexShrink: 0 }}
                    value={resultForm.new_visit_date || ''}
                    onChange={(e) => setResultForm((prev) => ({ ...prev, new_visit_date: e.target.value }))}
                  />
                  <button type="button" style={quickDateBtnStyle}
                    onClick={() => setResultForm((prev) => ({ ...prev, new_visit_date: addDaysYmd(getKstTodayYmd(), 1) }))}>
                    내일
                  </button>
                  <button type="button" style={quickDateBtnStyle}
                    onClick={() => setResultForm((prev) => ({ ...prev, new_visit_date: addDaysYmd(getKstTodayYmd(), 2) }))}>
                    모레
                  </button>
                </div>
                {resultForm.new_visit_date && (
                  <div style={subOptionSelectedStyle}>{resultForm.new_visit_date} 로 방문일자가 변경됩니다</div>
                )}
              </>
            )}
            {/* 날짜 미정 */}
            {resultForm.is_keep && (
              <div style={{ fontSize: 12, color: 'var(--mm-tone-amber-text)', lineHeight: 1.6, background: 'var(--mm-tone-amber-bg)', border: '1px solid var(--mm-tone-amber-border)', borderRadius: 8, padding: '8px 10px' }}>
                TM직원이 다시 연락하여 방문 일정을 잡습니다.
                {!adminMode && (
                  <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text-muted)' }}>
                    ※ 저장 후 해제는 TM직원만 가능합니다.
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── 미팅인계 서브옵션 ──────────────────────────────────── */}
        {resultForm.feedback === '미팅인계' && (
          <div style={subOptionWrapStyle}>
            <div style={modalSectionLabelStyle}>인계 담당자</div>
            <div style={salesPersonGridStyle}>
              {salesPersons.map((sp) => (
                <button
                  key={sp.id}
                  type="button"
                  style={salesPersonBtnStyle(resultForm.new_sales_person_name === sp.name)}
                  onClick={() => setResultForm((prev) => ({ ...prev, new_sales_person_name: sp.name }))}
                >
                  {sp.name}
                </button>
              ))}
            </div>
            {/* 인계받을 사람이 없을 때 빈칸만 두지 않는다 — 왜 비었는지 화면이 말해야 한다 */}
            {salesPersons.length === 0 && (
              <div style={emptyHintStyle}>
                인계할 수 있는 담당자가 없습니다. 미팅지도를 배정받은 같은 회사 직원만 인계 대상이 됩니다.
              </div>
            )}
            {resultForm.new_sales_person_name ? (
              <div style={subOptionSelectedStyle}>
                {resultForm.new_sales_person_name} 에게 인계됩니다
              </div>
            ) : salesPersons.length > 0 && (
              // 안 고르고 저장하면 "인계됨"으로만 기록되고 담당자는 그대로 남아, 그 미팅이
              //   알림 목록에서 영영 사라지지 않는다. 저장 전에 화면이 먼저 알려준다.
              <div style={warnHintStyle}>담당자를 선택해야 인계됩니다.</div>
            )}
          </div>
        )}

        {/* ── 메모 ────────────────────────────────────────────── */}
        <textarea
          rows={2}
          placeholder="메모 (선택사항)"
          style={feedbackMemoStyle}
          value={resultForm.feedback_memo || ''}
          onChange={(e) => setResultForm((prev) => ({ ...prev, feedback_memo: e.target.value }))}
          onFocus={(e) => {
            // 모바일 키보드가 올라온 뒤 textarea가 보이도록 스크롤
            const el = e.target;
            setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 350);
          }}
        />

        {/* ── 구분선 ──────────────────────────────────────────── */}
        <div style={modalDividerStyle} />

        {/* ── 계약완료 토글 ────────────────────────────────────── */}
        <button
          type="button"
          style={contractToggleBtnStyle(resultForm.is_contracted)}
          onClick={() => {
            const next = !resultForm.is_contracted;
            setResultForm((prev) => {
              const base = {
                ...prev,
                is_contracted: next,
                feedback: next ? '' : prev.feedback,
                contract_date: next && !prev.contract_date ? getKstTodayYmd() : prev.contract_date,
              };
              // 계약완료 ON + 상품 목록 이미 캐시된 경우: 유효기간 기본값 즉시 채우기
              if (next && contractTicketTypes.length > 0) {
                const newValidities = { ...prev.productValidities };
                (prev.selectedProducts || []).forEach((name) => {
                  if (!newValidities[name]) {
                    const product = contractTicketTypes.find((t) => t.name === name);
                    if (product?.default_validity_months != null) {
                      newValidities[name] = String(product.default_validity_months);
                    }
                  }
                });
                base.productValidities = newValidities;
              }
              return base;
            });
            if (next) fetchContractTicketTypes();
          }}
        >
          {resultForm.is_contracted ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
                <path d="M2 8l4 4 7-8" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              계약완료
            </span>
          ) : '계약완료'}
        </button>

        {/* ── 계약 상세 섹션 ───────────────────────────────────── */}
        {resultForm.is_contracted && (
          <ContractSection
            resultForm={resultForm}
            setResultForm={setResultForm}
            contractTicketTypes={contractTicketTypes}
            hasValiditySetting={hasValiditySetting}
          />
        )}

      </div>
    </Modal>
  );
}
